import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { v4 as uuidv4 } from 'uuid';

export interface IAChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  createdAt: string;
  attachments?: {
    id: number;
    label: string;
    type: 'pdf' | 'image';
    base64?: string;
  }[];
}

export interface IAConversation {
  id: string;
  savId: number;
  gestionnaireId: string;
  title: string;
  createdAt: string;
  updatedAt: string;
  messages: IAChatMessage[];
  isStreaming?: boolean;
  streamingContent?: string;
}

interface IAConversationsState {
  conversations: IAConversation[];
  addConversation: (savId: number, gestionnaireId: string, title?: string) => string;
  addMessage: (conversationId: string, message: Omit<IAChatMessage, 'id' | 'createdAt'>) => void;
  getConversationsBySav: (savId: number, gestionnaireId: string) => IAConversation[];
  setStreamingState: (conversationId: string, isStreaming: boolean, streamingContent?: string) => void;
}

function getCurrentUserId(): number | null {
  const user = localStorage.getItem('auth-storage');
  if (!user) return null;
  try {
    const parsed = JSON.parse(user);
    return parsed.state?.user?.id ?? null;
  } catch {
    return null;
  }
}

export const useIAConversationsStore = create<IAConversationsState>()(
  persist(
    (set, get) => ({
      conversations: [],
      addConversation: (savId, gestionnaireId, title) => {
        const id = uuidv4();
        const now = new Date().toISOString();
        const newConv: IAConversation = {
          id,
          savId,
          gestionnaireId,
          title: title || `Conversation du ${now}`,
          createdAt: now,
          updatedAt: now,
          messages: [],
        };
        set((state) => ({ conversations: [newConv, ...state.conversations] }));
        return id;
      },
      addMessage: (conversationId, message) => {
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === conversationId
              ? {
                  ...conv,
                  updatedAt: new Date().toISOString(),
                  messages: [
                    ...conv.messages,
                    {
                      ...message,
                      id: uuidv4(),
                      createdAt: new Date().toISOString(),
                    },
                  ],
                }
              : conv
          ),
        }));
      },
      getConversationsBySav: (savId, gestionnaireId) => {
        return get().conversations.filter((conv) => conv.savId === savId && conv.gestionnaireId === gestionnaireId);
      },
      setStreamingState: (conversationId, isStreaming, streamingContent) => {
        set((state) => ({
          conversations: state.conversations.map((conv) =>
            conv.id === conversationId
              ? {
                  ...conv,
                  isStreaming,
                  streamingContent: streamingContent ?? '',
                }
              : conv
          ),
        }));
      },
    }),
    {
      name: 'ia-conversations',
    }
  )
);

export { getCurrentUserId };